import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { TextInput } from '../../../input/TextInput'
import { NumberInput } from '../../../input/NumberInput'
import FormSection from '../../../formSection/FormSection'
import { InputDate } from '../../../input/inputDate/InputDate'
import { InputTime } from '../../../input/inputTime/InputTime'
import { WeekdaySelector } from '../../../weekdaySelector/WeekdaySelector'
import { MultiSelectInput } from '../../../input/multiSelectInput/MultiSelectInput'
import { SelectOptionInput } from '../../../input/selectOptionInput/SelectOptionInput'
import { isValidInterval } from '../../../input/intervalListInput/IntervalListInput'
import FormSectionActions from '../../../formSection/formSectionActions/FormSectionActions'

import { PriceRule } from '../../../../types/booking/booking'
import { ClubModelMetadata, CourtModel } from '../../../../types/court/court'
import { DayOfWeekOrder, formatDate } from '../../../../utils/dateUtils'
import { formatMinutes } from '../../../../utils/booking/booking'

export const emptyPriceRule = (club?: ClubModelMetadata, courts: CourtModel[] = []): PriceRule => ({
  clubId: club?.id ?? 0,
  name: '',
  courtIds: courts.filter(court => court.club?.id === club?.id).map(court => court.id!),
  weekdays: DayOfWeekOrder,
  interval: { startTime: club?.openTime?.slice(0, 5) ?? '08:00', endTime: club?.closeTime?.slice(0, 5) ?? '22:00' },
  effectiveFrom: formatDate(new Date()) ?? '',
  effectiveTo: null,
  currency: 'ron',
  amounts: (club?.sessionDurations ?? []).map(durationMinutes => ({ durationMinutes, amount: 0 })),
})

type PriceRuleFormProps = {
  initial: PriceRule
  clubs: ClubModelMetadata[]
  courts: CourtModel[]
  isEditMode: boolean
  loading: boolean
  onSave: (rule: PriceRule) => void
  onCancel: () => void
}

const toDate = (value?: string | null) => (value ? new Date(`${value}T00:00:00`) : null)

/**
 * Price of a single session by court, weekday and the interval containing the start time, per duration. Saving an
 * existing rule creates a new version; bookings keep the version they were quoted with.
 */
export const PriceRuleForm = ({ initial, clubs, courts, isEditMode, loading, onSave, onCancel }: PriceRuleFormProps) => {
  const { t } = useTranslation(['pricing', 'common'])
  const [rule, setRule] = useState<PriceRule>(initial)
  const dirty = useMemo(() => JSON.stringify(rule) !== JSON.stringify(initial), [rule, initial])
  const club = clubs.find(item => item.id === rule.clubId)

  const durations = useMemo(() => {
    const offered = club?.sessionDurations ?? []
    const existing = rule.amounts.map(amount => amount.durationMinutes)
    return Array.from(new Set([...offered, ...existing])).sort((a, b) => a - b)
  }, [club, rule.amounts])

  const courtOptions = courts
    .filter(court => court.club?.id === rule.clubId)
    .map(court => ({ value: String(court.id), label: court.name }))

  const amountOf = (duration: number) => rule.amounts.find(amount => amount.durationMinutes === duration)?.amount

  const setAmount = (duration: number, value: string) =>
    setRule(current => {
      const others = current.amounts.filter(amount => amount.durationMinutes !== duration)
      return {
        ...current,
        amounts:
          value === ''
            ? others
            : [...others, { durationMinutes: duration, amount: Number(value) }].sort(
                (a, b) => a.durationMinutes - b.durationMinutes
              ),
      }
    })

  const valid =
    rule.clubId > 0 &&
    rule.name.trim().length > 0 &&
    rule.courtIds.length > 0 &&
    rule.weekdays.length > 0 &&
    isValidInterval(rule.interval) &&
    Boolean(rule.effectiveFrom) &&
    (!rule.effectiveTo || rule.effectiveTo >= rule.effectiveFrom) &&
    rule.amounts.length > 0 &&
    rule.amounts.every(amount => Number.isFinite(amount.amount) && amount.amount >= 0)

  return (
    <FormSection
      title={t(isEditMode ? 'pricing.form.edit-title' : 'pricing.form.new-title')}
      subtitle={t(isEditMode ? 'pricing.form.edit-subtitle' : 'pricing.form.subtitle')}
      actions={
        <FormSectionActions
          isDirty={dirty && valid && !loading}
          loading={loading}
          onSave={() => onSave(rule)}
          onCancel={() => (isEditMode ? setRule(initial) : onCancel())}
          saveLabel={t('pricing.form.save-btn')}
          resetLabel={t(isEditMode ? 'pricing.form.reset-btn' : 'pricing.form.cancel-btn')}
        />
      }
    >
      <div className="row g-3 manager-form">
        <div className="col-12 col-md-6">
          <SelectOptionInput
            name="clubId"
            label={t('pricing.form.fields.club')}
            value={rule.clubId ? String(rule.clubId) : ''}
            list={clubs.map(item => ({ value: String(item.id), label: item.name }))}
            disabled={isEditMode}
            onChange={(_, value) => {
              const nextClub = clubs.find(item => String(item.id) === value)
              setRule(current => ({ ...emptyPriceRule(nextClub, courts), name: current.name }))
            }}
            required
          />
        </div>
        <div className="col-12 col-md-6">
          <TextInput
            name="name"
            label={t('pricing.form.fields.name')}
            value={rule.name}
            onChange={(_, value) => setRule(current => ({ ...current, name: value }))}
            errorMessage={t('pricing.validation.required')}
            required
          />
        </div>
        <div className="col-12">
          <MultiSelectInput
            name="courtIds"
            label={t('pricing.form.fields.courts')}
            value={courtOptions.filter(option => rule.courtIds.includes(Number(option.value)))}
            options={courtOptions}
            onChange={(_, values) => setRule(current => ({ ...current, courtIds: values.map(Number) }))}
            errorMessage={t('pricing.validation.courts')}
            required
          />
        </div>
        <div className="col-12">
          <WeekdaySelector
            days={rule.weekdays}
            disabled={loading}
            label={t('pricing.form.fields.weekdays')}
            onChange={weekdays => setRule(current => ({ ...current, weekdays }))}
          />
        </div>
        <div className="col-12 col-md-6">
          <InputTime
            name="startTime"
            label={t('pricing.form.fields.start-time')}
            value={rule.interval.startTime}
            infoMessage={t('pricing.form.fields.interval-info')}
            onChange={(_, value) => setRule(current => ({ ...current, interval: { ...current.interval, startTime: value } }))}
            required
          />
        </div>
        <div className="col-12 col-md-6">
          <InputTime
            name="endTime"
            label={t('pricing.form.fields.end-time')}
            value={rule.interval.endTime}
            errorMessage={isValidInterval(rule.interval) ? undefined : t('common:interval-input.invalid')}
            onChange={(_, value) => setRule(current => ({ ...current, interval: { ...current.interval, endTime: value } }))}
            required
          />
        </div>
        <div className="col-12 col-md-6">
          <InputDate
            name="effectiveFrom"
            label={t('pricing.form.fields.effective-from')}
            value={toDate(rule.effectiveFrom)}
            onChange={(_, value) => setRule(current => ({ ...current, effectiveFrom: formatDate(value ?? undefined) ?? '' }))}
            required
          />
        </div>
        <div className="col-12 col-md-6">
          <InputDate
            name="effectiveTo"
            label={t('pricing.form.fields.effective-to')}
            value={toDate(rule.effectiveTo)}
            infoMessage={t('pricing.form.fields.effective-to-info')}
            minDate={toDate(rule.effectiveFrom) ?? undefined}
            onChange={(_, value) => setRule(current => ({ ...current, effectiveTo: formatDate(value ?? undefined) ?? null }))}
          />
        </div>
        <div className="col-12">
          <div className="eyebrow mb-8">{t('pricing.form.fields.amounts')}</div>
          {durations.length === 0 && <div className="muted">{t('pricing.form.no-durations')}</div>}
          <div className="row g-3">
            {durations.map(duration => (
              <div className="col-12 col-md-4" key={duration}>
                <NumberInput
                  name={`amount-${duration}`}
                  label={t('pricing.form.fields.amount-for', { duration: formatMinutes(duration) })}
                  value={amountOf(duration) === undefined ? '' : String(amountOf(duration))}
                  min={0}
                  step={0.01}
                  onChange={(_, value) => setAmount(duration, value)}
                  onValidate={value => Number(value) >= 0}
                  errorMessage={t('pricing.validation.non-negative')}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </FormSection>
  )
}
